import {
  TextField as AriaTextField,
  ValidationResult,
} from 'react-aria-components';
import React, { useId, ChangeEvent, InvalidEvent, ForwardedRef } from 'react';
import FieldHeader from '../FieldHeader';
import styles from './SelectNative.module.css';

export interface SelectNativeOption {
  label: string;
  value: string;
}

export interface SelectNativeProps {
  /**
   * The label for the SelectNative
   */
  label?: string;
  /**
   * The description for the SelectNative
   */
  description?: string;
  /**
   * The error message for the SelectNative
   */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /**
   * Whether the SelectNative is invalid
   * @default false
   */
  isInvalid?: boolean;
  /**
   * Whether the SelectNative is disabled
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the SelectNative is required
   * @default false
   */
  isRequired?: boolean;
  /**
   * Placeholder text when no option is selected
   */
  placeholder?: string;
  /**
   * Whether the placeholder should be visible in the dropdown list
   * @default false
   */
  showPlaceholderInDropdown?: boolean;
  /**
   * The options for the SelectNative
   */
  options: SelectNativeOption[];
  /**
   * The default selected value
   * @default ""
   */
  defaultValue?: string;
  /**
   * The current value (for controlled components)
   */
  value?: string;
  /**
   * Optional ID for the select element. If not provided, one will be generated.
   */
  id?: string;
  /**
   * Callback function when the selection changes
   */
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  /**
   * Callback function when the select value is invalid when a form is submitted
   */
  onInvalid?: (event: InvalidEvent<HTMLSelectElement>) => void;
  /**
   * HTML autocomplete token for browser autofill.
   */
  autoComplete?: string;
  /**
   * Test id for the component
   */
  'data-testid'?: string;
}

const SelectNative = React.forwardRef<HTMLSelectElement, SelectNativeProps>(
  (
    {
      label,
      description,
      errorMessage,
      isInvalid = false,
      isDisabled = false,
      isRequired = false,
      placeholder,
      options,
      defaultValue,
      value,
      id: providedId,
      onChange,
      onInvalid,
      autoComplete,
      showPlaceholderInDropdown = false,
      'data-testid': dataTestId,
      ...props
    },
    ref: ForwardedRef<HTMLSelectElement>,
  ) => {
    const uniqueId = useId();
    const id = providedId || `select-${uniqueId}`;
    const showPlaceholder =
      !value && (defaultValue === undefined || defaultValue === '');

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        onChange(event);
      }
    };

    // Controlled/uncontrolled value logic for the select element
    // If value is provided, use controlled mode; otherwise, use defaultValue for uncontrolled
    const selectValueProps =
      value !== undefined
        ? { value }
        : defaultValue !== undefined
          ? { defaultValue }
          : {};

    return (
      <AriaTextField
        className={styles.selectNative}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        isRequired={isRequired}
        data-testid={dataTestId}
        {...props}
      >
        {label && (
          <FieldHeader
            label={label}
            description={description}
            isInvalid={isInvalid}
            isRequired={isRequired}
            errorMessage={errorMessage}
            labelFor={id}
          />
        )}
        <select
          ref={ref}
          id={id}
          className={styles['selectNative__input']}
          {...selectValueProps}
          disabled={isDisabled}
          required={isRequired}
          aria-invalid={isInvalid}
          autoComplete={autoComplete || undefined}
          onChange={handleChange}
          onInvalid={onInvalid}
        >
          {(showPlaceholder || showPlaceholderInDropdown) && placeholder && (
            <option
              value=""
              disabled={!!showPlaceholderInDropdown}
              hidden={!showPlaceholderInDropdown}
            >
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </AriaTextField>
    );
  },
);

SelectNative.displayName = 'SelectNative';

export default SelectNative;
